import { z } from 'zod';

/**
 * Zod schemas for report-related entities
 * These provide runtime validation and type inference
 */

// Report schema
export const reportSchema = z.object({
  id: z.string(),
  type: z.enum(['ProfitLoss', 'BalanceSheet', 'CashFlow', 'RentRoll', 'Occupancy', 'Maintenance', 'OwnerStatement', 'TenantLedger', 'VendorPerformance', 'ApplicationFunnel', 'ApplicationSourceAnalysis', 'ScreeningPerformance', 'Custom'] as const),
  name: z.string().min(1, 'Report name is required'),
  description: z.string().optional(),
  parameters: z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    /** Tenant ledger: single tenant id */
    tenantId: z.string().optional(),
    propertyIds: z.array(z.string()).optional(),
    tenantIds: z.array(z.string()).optional(),
    ownerIds: z.array(z.string()).optional(),
    vendorIds: z.array(z.string()).optional(),
    /** P&amp;L / reporting: operational (default) vs journal-entry source */
    dataSource: z.enum(['operational', 'journal']).optional(),
    /** Operational P&amp;L: cash vs accrual (default accrual) */
    accountingBasis: z.enum(['cash', 'accrual']).optional(),
    /** Journal P&amp;L: Cash | Accrual ledger basis */
    journalBasis: z.enum(['Cash', 'Accrual']).optional(),
    /** @deprecated Prefer dataSource + journalBasis */
    basis: z.enum(['Cash', 'Accrual']).optional(),
  }).catchall(z.unknown()), // Allow custom parameters
  status: z.enum(['Pending', 'Generating', 'Completed', 'Failed'] as const),
  progress: z.number().int().min(0).max(100).optional(),
  data: z.record(z.unknown()).optional(),
  format: z.enum(['PDF', 'CSV', 'Excel', 'JSON'] as const).optional(),
  fileUrl: z
    .union([z.string().url(), z.literal('')])
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
  fileSize: z.number().nonnegative().optional(),
  scheduled: z.boolean().optional(),
  scheduleConfig: z.object({
    frequency: z.enum(['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'] as const),
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    time: z.string().optional(),
    recipients: z.array(z.string().email()).optional(),
  }).optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  generatedAt: z.string().optional(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
});

export type Report = z.infer<typeof reportSchema>;

// CustomReportField schema
const customReportFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  dataSource: z.enum(['tenants', 'properties', 'bills', 'receivables', 'leases', 'tickets', 'applications', 'payments', 'journalEntries'] as const),
  aggregation: z.enum(['sum', 'avg', 'count', 'min', 'max', 'none'] as const).optional(),
  format: z.enum(['currency', 'date', 'number', 'percentage', 'text'] as const).optional(),
});

// CustomReportFilter schema
const customReportFilterSchema = z.object({
  field: z.string(),
  operator: z.enum(['equals', 'notEquals', 'contains', 'notContains', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'in', 'notIn', 'isEmpty', 'isNotEmpty'] as const),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null(), z.undefined()]),
  value2: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]).optional(),
});

// CustomReportGrouping schema
const customReportGroupingSchema = z.object({
  field: z.string(),
  order: z.enum(['asc', 'desc'] as const),
});

// CustomReportSorting schema
const customReportSortingSchema = z.object({
  field: z.string(),
  order: z.enum(['asc', 'desc'] as const),
});

// CustomReport schema
export const customReportSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Report name is required'),
  description: z.string().optional(),
  dataSource: z.enum(['tenants', 'properties', 'bills', 'receivables', 'leases', 'tickets', 'applications', 'payments', 'journalEntries'] as const),
  fields: z.array(customReportFieldSchema),
  filters: z.array(customReportFilterSchema),
  grouping: z.array(customReportGroupingSchema).optional(),
  sorting: z.array(customReportSortingSchema).optional(),
  dateRange: z.object({
    field: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }).optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  scheduleConfig: z.object({
    frequency: z.enum(['Daily', 'Weekly', 'Monthly'] as const),
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    time: z.string().optional(),
    recipients: z.array(z.string().email()).optional(),
  }).optional(),
});

export type CustomReport = z.infer<typeof customReportSchema>;

// Report generation request schema - validates input at API boundary
export const reportGenerateRequestSchema = z.object({
  type: z.enum(['ProfitLoss', 'BalanceSheet', 'CashFlow', 'RentRoll', 'Occupancy', 'Maintenance', 'OwnerStatement', 'TenantLedger', 'VendorPerformance', 'ApplicationFunnel', 'ApplicationSourceAnalysis', 'ScreeningPerformance', 'Custom'] as const, {
    errorMap: () => ({ message: 'Invalid report type. Please select a supported report type.' })
  }),
  parameters: z.record(z.unknown()),
  name: z.string().optional(),
});

export type ReportGenerateRequest = z.infer<typeof reportGenerateRequestSchema>;
